import { ethers } from "ethers";
import parsePhoneNumberFromString, {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength
} from "libphonenumber-js";
import dayjs from "dayjs";
import { rainbowKitConfig } from "./config";
import { useReadContract } from "wagmi";
import { parseUnits } from "viem";
import { readContract } from "@wagmi/core";

export const passwordLength = 6;

export const validateName = (name) => {
  const re =
    /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u;
  return re.test(name);
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const validatePassword = (password) => {
  const re =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  return re.test(password);
};

export const tabs = [
  { id: "collectible", label: "Collectible" },
  { id: "on-sale", label: "On Sale" },
  { id: "activity", label: "Activity" },
  { id: "physical-asset", label: "Physical Asset" },
  { id: "track-asset", label: "Track Asset" }
];

export const formatWalletAddress = (walletAddress) => {
  if (walletAddress) {
    const firstPart = walletAddress.slice(0, 4); // First 4 characters
    const lastPart = walletAddress.slice(-4); // Last 4 characters

    return `${firstPart}........${lastPart}`;
  } else {
    return "--";
  }
};

export const getDaysAgoText = (createdAt) => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffInMs = now - createdDate;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Bought today";
  if (diffInDays === 1) return "Bought 1 day ago";
  return `Bought ${diffInDays} days ago`;
};

export const validInternationalPhone = (input, country) => {
  let formattedInput = input;

  // Handle common country codes
  if (!input.startsWith("+")) {
    const countryPrefixes = {
      IN: "91",
      US: "1",
      CA: "1",
      GB: "44",
      AU: "61"
    };

    const prefix = countryPrefixes[country?.toUpperCase()];
    if (prefix && input.startsWith(prefix)) {
      formattedInput = `+${input}`;
    }
  }

  const phone = parsePhoneNumberFromString(formattedInput, country);
  if (!phone) {
    console.log("❌ Could not parse phone");
    return false;
  }

  const isPossible = isPossiblePhoneNumber(formattedInput, country);
  const isValid = isValidPhoneNumber(formattedInput, country);
  const lengthValidation = validatePhoneNumberLength(formattedInput, country);

  return (
    isPossible === true && isValid === true && lengthValidation === undefined
  );
};

export const formatBalance = (balance) => {
  const num = Number(balance);
  if (isNaN(num) || num === 0) return "0";

  // Handle large numbers
  if (num >= 1000) {
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  // Handle small numbers with increasing precision
  let precision = 2;
  while (num < 1 && precision < 14) {
    precision++;
    if (Number(num.toFixed(precision)) !== 0) break;
  }

  return num.toFixed(precision);
};

export const getAmountsOut = async (
  amountIn,
  fromToken,
  toToken,
  decimals = 18
) => {
  try {
    const TESTNET_ROUTER_ADDRESS = process.env.REACT_APP_TESTNET_ROUTER_ADDRESS;

    if (!TESTNET_ROUTER_ADDRESS) {
      throw new Error("Router address missing in environment variables.");
    }

    const path = [fromToken, toToken];
    const parsedAmountIn = parseUnits(amountIn, decimals);

    const data = await readContract(rainbowKitConfig, {
      address: TESTNET_ROUTER_ADDRESS,
      abi: [
        {
          inputs: [
            { internalType: "uint256", name: "amountIn", type: "uint256" },
            { internalType: "address[]", name: "path", type: "address[]" }
          ],
          name: "getAmountsOut",
          outputs: [
            {
              internalType: "uint256[]",
              name: "amounts",
              type: "uint256[]"
            }
          ],
          stateMutability: "view",
          type: "function"
        }
      ],
      functionName: "getAmountsOut",
      args: [parsedAmountIn, path]
    });

    const rawOut = data[1];
    const formatted = Number(rawOut) / 10 ** decimals;

    return formatted.toString();
  } catch (error) {
    console.error("Error fetching getAmountsOut:", error);
    return "0";
  }
};

export const formatNumber = (number) => {
  // Handle non-number inputs
  if (typeof number !== "number" || isNaN(number)) {
    return "0";
  }

  const absNumber = Math.abs(number);
  const sign = number < 0 ? "-" : "";

  if (absNumber >= 1_000_000) {
    return sign + (absNumber / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (absNumber >= 1_000) {
    return sign + (absNumber / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  } else {
    return number.toString();
  }
};

export const getRemainingDays = (createdAt, numberOfDays) => {
  const startDate = dayjs(createdAt);

  const isLocal = process.env.REACT_APP_ENVIRONMENT === "local";

  const endDate = isLocal
    ? startDate.add(numberOfDays, "minute")
    : startDate.add(numberOfDays, "day");

  const now = dayjs();
  const remaining = isLocal
    ? endDate.diff(now, "minute")
    : endDate.diff(now, "day");

  return remaining > 0 && remaining !== 0 ? `${remaining} Days` : "Completed";
};

export const isPlanCompleted = (createdAt, numberOfDays) => {
  return getRemainingDays(createdAt, numberOfDays) === "Completed";
};

export function shortenAddress(address, chars = 4) {
  if (!address) return "";
  return address.slice(0, chars + 2) + "..." + address.slice(-chars);
}
